import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'styles/utils/classNames';
import { CardSectionMode, CardsSectionProps } from './props';
import { CardWrapper, Container } from './styles';
import { loadingSkeletonGrid } from './utils';

const CardsSection = <T,>({
  data,
  renderCard,
  visibleCards,
  className,
  cardsWrapperClassName,
  showArrowsOnMobile = false,
  loading = false,
  scrollOffset = { base: 0 },
  prevArrow,
  nextArrow,
  gap,
  mode = { base: CardSectionMode.HORIZONTAL },
  externalPrevArrowRef,
  externalNextArrowRef,
  prefix,
  affix,
}: CardsSectionProps<T>) => {
  const [showPrevArrow, setShowPrevArrow] = useState<boolean>(false);
  const [showNextArrow, setShowNextArrow] = useState<boolean>(true);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback((direction: 'prev' | 'next') => {
    let cardSize = 0;
    let isHorizontal = true;

    if (containerRef.current?.firstElementChild) {
      const firstCard = containerRef.current.firstElementChild as HTMLElement;

      const containerStyle = getComputedStyle(containerRef.current);
      isHorizontal = containerStyle.flexDirection === 'row';

      cardSize = isHorizontal ? firstCard.offsetWidth : firstCard.offsetHeight;
    }

    if (containerRef.current && cardSize > 0) {
      const scrollAmount = direction === 'next' ? cardSize : -cardSize;

      const scrollOptions: ScrollToOptions = isHorizontal
        ? { left: scrollAmount, behavior: 'smooth' }
        : { top: scrollAmount, behavior: 'smooth' };

      containerRef.current.scrollBy(scrollOptions);
    }
  }, []);

  const updateArrowVisibility = useCallback(() => {
    if (containerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
      const prevVisible = scrollLeft > 0;
      const nextVisible = scrollLeft + clientWidth < scrollWidth;

      setShowPrevArrow(prevVisible);
      setShowNextArrow(nextVisible);

      // Update external arrow visibility if refs are provided
      if (externalPrevArrowRef?.current) {
        if (prevVisible) {
          externalPrevArrowRef.current.classList.remove('opacity-50', 'cursor-default');
          externalPrevArrowRef.current.classList.add('cursor-pointer');
          externalPrevArrowRef.current.removeAttribute('disabled');
        } else {
          externalPrevArrowRef.current.classList.add('opacity-50', 'cursor-default');
          externalPrevArrowRef.current.classList.remove('cursor-pointer');
          externalPrevArrowRef.current.setAttribute('disabled', 'true');
        }
      }

      if (externalNextArrowRef?.current) {
        if (nextVisible) {
          externalNextArrowRef.current.classList.remove('opacity-50', 'cursor-default');
          externalNextArrowRef.current.classList.add('cursor-pointer');
          externalNextArrowRef.current.removeAttribute('disabled');
        } else {
          externalNextArrowRef.current.classList.add('opacity-50', 'cursor-default');
          externalNextArrowRef.current.classList.remove('cursor-pointer');
          externalNextArrowRef.current.setAttribute('disabled', 'true');
        }
      }
    }
  }, [externalPrevArrowRef, externalNextArrowRef]);

  // Bind click handlers to external arrow refs
  useEffect(() => {
    const handlePrevClick = () => handleScroll('prev');
    const handleNextClick = () => handleScroll('next');

    // Attach event listeners to external arrows if provided
    if (externalPrevArrowRef?.current) {
      externalPrevArrowRef.current.addEventListener('click', handlePrevClick);
    }

    if (externalNextArrowRef?.current) {
      externalNextArrowRef.current.addEventListener('click', handleNextClick);
    }

    updateArrowVisibility();

    return () => {
      if (externalPrevArrowRef?.current) {
        externalPrevArrowRef.current.removeEventListener('click', handlePrevClick);
      }

      if (externalNextArrowRef?.current) {
        externalNextArrowRef.current.removeEventListener('click', handleNextClick);
      }
    };
  }, [handleScroll, updateArrowVisibility, externalPrevArrowRef, externalNextArrowRef]);

  useEffect(() => {
    updateArrowVisibility();

    const handleScrollEvent = () => {
      updateArrowVisibility();
    };

    containerRef.current?.addEventListener('scroll', handleScrollEvent);

    return () => {
      containerRef.current?.removeEventListener('scroll', handleScrollEvent);
    };
  }, [data, updateArrowVisibility]);

  return (
    <div className={className}>
      <div className="relative h-full w-full">
        {!loading && prevArrow && showPrevArrow && !externalPrevArrowRef && (
          <span
            className={classNames(
              'absolute left-4 top-1/2 z-10 -translate-y-1/2 cursor-pointer select-none',
              showArrowsOnMobile === false && 'max-sm:hidden',
            )}
            onClick={() => handleScroll('prev')}
          >
            {prevArrow}
          </span>
        )}
        {!loading && nextArrow && showNextArrow && !externalNextArrowRef && (
          <span
            className={classNames(
              'absolute right-4 top-1/2 z-10 -translate-y-1/2 cursor-pointer select-none',
              showArrowsOnMobile === false && 'max-sm:hidden',
            )}
            onClick={() => handleScroll('next')}
          >
            {nextArrow}
          </span>
        )}
        <Container
          ref={containerRef}
          gap={gap}
          mode={mode}
          visibleCards={visibleCards}
          scrollOffset={scrollOffset}
          className={cardsWrapperClassName}
        >
          {/* Prefix at the start */}
          {prefix && data.length > 0 && (
            <CardWrapper
              visibleCards={visibleCards}
              scrollOffset={scrollOffset}
              mode={mode}
              gap={gap}
            >
              {prefix}
            </CardWrapper>
          )}

          {/* Regular cards */}
          {loading &&
            loadingSkeletonGrid.map((Item, index) => (
              <CardWrapper
                key={index}
                visibleCards={visibleCards}
                scrollOffset={scrollOffset}
                mode={mode}
                gap={gap}
              >
                <Item />
              </CardWrapper>
            ))}

          {!loading &&
            data.map((item, index) => (
              <CardWrapper
                key={index}
                visibleCards={visibleCards}
                scrollOffset={scrollOffset}
                mode={mode}
                gap={gap}
              >
                {renderCard(item, index)}
              </CardWrapper>
            ))}

          {/* Affix at the end */}
          {affix && data.length > 0 && (
            <CardWrapper
              visibleCards={visibleCards}
              scrollOffset={scrollOffset}
              mode={mode}
              gap={gap}
            >
              {affix}
            </CardWrapper>
          )}
        </Container>
      </div>
    </div>
  );
};

export default CardsSection;
